import { useState } from 'react';
import { isAxiosError } from 'axios';
import { Button } from '@/design-system/Button';
import { Card } from '@/design-system/Card';
import { Input } from '@/design-system/Input';
import { useAuth } from '@/core/auth';
import { authService, type TwoFactorSetup } from '@/features/auth';

function errMsg(err: unknown, fallback: string) {
  const m = isAxiosError(err)
    ? (err.response?.data as { message?: string } | undefined)?.message
    : err instanceof Error
      ? err.message
      : undefined;
  return m || fallback;
}

/**
 * Gerenciamento do 2FA (TOTP) do próprio staff — KLOCKSEC.
 * Ativar (QR + confirmar + backup codes) ou desativar (re-auth por senha).
 */
export function TwoFactorSection() {
  const { user, refreshUser } = useAuth();
  const enabled = !!user?.twoFactorEnabled;

  const [setup, setSetup] = useState<TwoFactorSetup | null>(null);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [disablePassword, setDisablePassword] = useState('');
  const [showDisable, setShowDisable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const startSetup = async () => {
    setError('');
    setBusy(true);
    try {
      setSetup(await authService.setup2fa());
    } catch (err) {
      setError(errMsg(err, 'Erro ao iniciar 2FA'));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setError('');
    setBusy(true);
    try {
      const codes = await authService.confirm2fa(code.trim());
      setBackupCodes(codes);
      setSetup(null);
      setCode('');
      await refreshUser();
    } catch (err) {
      setError(errMsg(err, 'Código inválido'));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setError('');
    setBusy(true);
    try {
      await authService.disable2fa(disablePassword);
      setShowDisable(false);
      setDisablePassword('');
      await refreshUser();
    } catch (err) {
      setError(errMsg(err, 'Erro ao desativar 2FA'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="max-w-xl">
      <h2 className="text-lg font-semibold text-gray-900">Verificação em duas etapas (2FA)</h2>
      <p className="text-sm text-gray-600 mt-1">
        Protege o acesso administrativo exigindo um código do app autenticador além da senha.
      </p>

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</div>
      )}

      {/* Backup codes recém-gerados */}
      {backupCodes && (
        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm font-medium text-amber-900">Guarde seus backup codes (mostrados só uma vez):</p>
          <div className="mt-2 grid grid-cols-2 gap-1 font-mono text-sm text-amber-900">
            {backupCodes.map((c) => (
              <span key={c}>{c}</span>
            ))}
          </div>
          <Button variant="outline" className="mt-3" onClick={() => setBackupCodes(null)}>
            Já salvei
          </Button>
        </div>
      )}

      {/* Estado: ativo */}
      {enabled && !backupCodes && (
        <div className="mt-4">
          <span className="inline-block px-2 py-0.5 text-xs font-medium text-green-700 bg-green-100 rounded-md">
            2FA ativo
          </span>
          {!showDisable ? (
            <div className="mt-3">
              <Button variant="danger" onClick={() => setShowDisable(true)}>
                Desativar 2FA
              </Button>
            </div>
          ) : (
            <div className="mt-3 space-y-3">
              <Input
                label="Confirme sua senha para desativar"
                type="password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
              />
              <div className="flex gap-2">
                <Button variant="danger" onClick={() => void disable()} isLoading={busy} disabled={!disablePassword}>
                  Desativar
                </Button>
                <Button variant="outline" onClick={() => setShowDisable(false)} disabled={busy}>
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Estado: inativo */}
      {!enabled && !backupCodes && (
        <div className="mt-4">
          {!setup ? (
            <Button variant="primary" onClick={() => void startSetup()} isLoading={busy}>
              Ativar 2FA
            </Button>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                1. Escaneie o QR no seu app autenticador (Google Authenticator, Authy, 1Password…):
              </p>
              <img src={setup.qrCodeDataUrl} alt="QR Code 2FA" className="w-44 h-44 border rounded-lg" />
              <p className="text-xs text-gray-500 break-all">
                Ou insira a chave manualmente: <span className="font-mono">{setup.secret}</span>
              </p>
              <p className="text-sm text-gray-700">2. Informe o código gerado para confirmar:</p>
              <Input
                label="Código"
                type="text"
                inputMode="numeric"
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              <div className="flex gap-2">
                <Button variant="primary" onClick={() => void confirm()} isLoading={busy} disabled={code.length < 6}>
                  Confirmar e ativar
                </Button>
                <Button variant="outline" onClick={() => { setSetup(null); setCode(''); }} disabled={busy}>
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
